/**
 * Adopt a `window.open` handle for a detached surface (见 18 游离窗).
 *
 * A handle that is not the workbench itself is a real window: we never close
 * it or fall back in-page. Fresh `about:blank` is the normal first paint of
 * `window.open(url)` — do not `replace` it, or the first navigation and the
 * opener's SharedWorker port get torn down. Only a finished page that still
 * has no `?surface=` (reused window name) gets `location.replace`.
 * If the handle is the current workbench, treat it as blocked so IM and
 * other overlays keep their page.
 */
import { parseSurfaceTargetFromHref } from '@hibro/shared';

export type DetachedWindowAdoption = 'window' | 'blocked';

function isAboutBlank(href: string): boolean {
  return href === 'about:blank' || href.startsWith('about:blank');
}

export function needsSurfaceNavigation(opened: Window): boolean {
  if (opened.closed) {
    return false;
  }
  let href: string;
  let readyState: DocumentReadyState;
  try {
    href = opened.location.href;
    readyState = opened.document.readyState;
  } catch {
    return false;
  }
  if (readyState !== 'complete' || isAboutBlank(href)) {
    return false;
  }
  return parseSurfaceTargetFromHref(href) === null;
}

export function adoptOpenedDetachedWindow(
  opened: Window | null,
  expectedUrl: string,
): DetachedWindowAdoption {
  if (!opened || opened.closed || opened === window) {
    return 'blocked';
  }
  if (needsSurfaceNavigation(opened)) {
    try {
      opened.location.replace(expectedUrl);
    } catch {
      // Cross-origin or a racing navigation: the handle still stands.
    }
  }
  return 'window';
}

export type FocusedDetachedAdoption = 'focused' | 'miss';

/**
 * Adopt a handle from `window.open('', name)` (focus, no URL).
 *
 * A live `?surface=` window is focused. `about:blank` is a leftover or a
 * newly spawned blank tab — close it, never leave it. Other same-origin
 * pages without a surface are a miss; do not close them (they may be the
 * workbench).
 */
export function adoptFocusedDetachedWindow(
  opened: Window | null,
): FocusedDetachedAdoption {
  if (!opened || opened.closed || opened === window) {
    return 'miss';
  }
  let href: string;
  try {
    href = opened.location.href;
  } catch {
    opened.focus();
    return 'focused';
  }
  if (parseSurfaceTargetFromHref(href) !== null) {
    opened.focus();
    return 'focused';
  }
  if (isAboutBlank(href)) {
    opened.close();
    return 'miss';
  }
  return 'miss';
}
